"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { Input } from "./input";
import { LiquidPill } from "./liquid-pill";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/v2/select";
import { SlidersHorizontal } from "lucide-react";
import useTranslation from "next-translate/useTranslation";

export interface FilterItem {
  id: string;
  label: string;
  type: "input" | "select" | "custom";
  value: string;
  onChange: (value: string) => void;
  options?: { label: string; value: string }[];
  placeholder?: string;
  advanced?: boolean;
  render?: () => React.ReactNode;
}

export interface PageFiltersProps {
  filters: FilterItem[];
  enableAdvanced?: boolean;
  /** "grid" (default) keeps the legacy stacked layout. "toolbar" renders the
   * basic filters as a single responsive flex row, with `actions` pushed right. */
  variant?: "grid" | "toolbar";
  /** Optional action buttons rendered at the right edge of the toolbar row.
   * Only used when variant === "toolbar". */
  actions?: React.ReactNode;
  className?: never;
}

export function PageFilters({
  filters,
  enableAdvanced = false,
  variant = "grid",
  actions,
  className,
}: PageFiltersProps) {
  const { t } = useTranslation("common");
  const [showAdvanced, setShowAdvanced] = React.useState(false);

  const basicFilters = filters.filter((f) => !f.advanced);
  const advancedFilters = filters.filter((f) => f.advanced);

  const isToolbar = variant === "toolbar";

  // En modo toolbar cada control usa el ancho canónico de filtros (w-64),
  // igual que SelectPickerV2/TagPickerV2/MultiSelectFilterV2 size="filter".
  const toolbarItemClass = (_filter: FilterItem) => "w-64 shrink-0";

  const renderFilter = (filter: FilterItem) => {
    const wrapperClass = isToolbar ? toolbarItemClass(filter) : undefined;

    if (filter.render)
      return (
        <div key={filter.id} className={wrapperClass}>
          {filter.render()}
        </div>
      );

    if (filter.type === "input") {
      return (
        <div key={filter.id} className={wrapperClass}>
          <Input
            label={filter.label}
            value={filter.value}
            onChange={(e) => filter.onChange(e.target.value)}
            size="filter"
          />
        </div>
      );
    }

    if (filter.type === "select") {
      return (
        <div key={filter.id} className={wrapperClass}>
          <Select
            value={filter.value || undefined}
            onValueChange={filter.onChange}
          >
            <SelectTrigger
              label={filter.label}
              size="filter"
              floating={Boolean(filter.value)}
            >
              <SelectValue placeholder={filter.placeholder || filter.label} />
            </SelectTrigger>
            <SelectContent>
              {filter.options?.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    return null;
  };

  const advancedToggle = enableAdvanced && advancedFilters.length > 0 && (
    <LiquidPill
      as="button"
      type="button"
      shape="circle"
      size="sm"
      variant={showAdvanced ? "indigo" : "slate"}
      appearance="tinted"
      shadow="neutral"
      active={showAdvanced}
      icon={<SlidersHorizontal className="h-3.5 w-3.5" />}
      onClick={() => setShowAdvanced(!showAdvanced)}
      aria-label={showAdvanced ? t("Ocultar filtros avanzados") : t("Filtros avanzados")}
    />
  );

  if (isToolbar) {
    return (
      <div className={cn("w-full min-w-0 space-y-3", className)}>
        {actions && (
          <div className="flex w-full min-w-0 flex-wrap items-center justify-end gap-2">
            {actions}
          </div>
        )}
        <div className="flex w-full min-w-0 flex-wrap items-center gap-2">
          {basicFilters.map(renderFilter)}
          {advancedToggle}
        </div>

        {enableAdvanced && advancedFilters.length > 0 && showAdvanced && (
          <div className="rounded-2xl border border-slate-200/70 bg-white/90 p-5 shadow-[0_6px_28px_-16px_rgba(15,23,42,0.35)] dark:border-zinc-800 dark:bg-zinc-900/85">
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-700/95 dark:text-zinc-200">
              <SlidersHorizontal className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
              {t("Filtros Avanzados")}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {advancedFilters.map(renderFilter)}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {basicFilters.map(renderFilter)}
      </div>

      {enableAdvanced && advancedFilters.length > 0 && (
        <>
          <div className="flex justify-end">{advancedToggle}</div>

          {showAdvanced && (
            <div className="rounded-2xl border border-slate-200/70 bg-white/90 p-5 shadow-[0_6px_28px_-16px_rgba(15,23,42,0.35)] dark:border-zinc-800 dark:bg-zinc-900/85">
              <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-700/95 dark:text-zinc-200">
                <SlidersHorizontal className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
                {t("Filtros Avanzados")}
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {advancedFilters.map(renderFilter)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
